import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  SlidersHorizontal,
  Copy,
  RefreshCw,
  ShieldAlert,
  Building2,
  Pencil,
  KeyRound,
  Power,
  Trash2,
  Users,
} from "lucide-react";
import {
  listPlatformUsers,
  createPlatformUser,
  updatePlatformUser,
  deletePlatformUser,
  listTenants,
  type CurrentUser,
  type PlatformUserItem,
  type Tenant,
} from "../../lib/api";


export function TeamManagementView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTenantFilter, setSelectedTenantFilter] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "tenant" | "agent">("agent");
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user.tenant_id || "");

  // Edit User state
  const [editingUser, setEditingUser] = useState<PlatformUserItem | null>(null);
  const [editRole, setEditRole] = useState<"admin" | "tenant" | "agent">("agent");
  const [editTenantId, setEditTenantId] = useState<string>("");

  // Password Reset state
  const [resetPasswordUser, setResetPasswordUser] = useState<PlatformUserItem | null>(null);
  const [newResetPassword, setNewResetPassword] = useState("");

  // Live Users Query from Backend
  const usersQuery = useQuery({
    queryKey: ["platform-users", token],
    queryFn: () => listPlatformUsers(token),
    refetchInterval: 5_000,
  });

  // Live Tenants Query
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list"],
    queryFn: () => listTenants(token),
  });

  const users = usersQuery.data || [];
  const tenants = tenantsQuery.data || [];

  // Create User Mutation
  const createUserMutation = useMutation({
    mutationFn: () =>
      createPlatformUser(token, {
        username: newUsername.trim(),
        password: newPassword.trim(),
        role: newUserRole,
        tenant_id: newUserRole === "admin" ? undefined : (isSuperAdmin ? (selectedTenantId || undefined) : user.tenant_id),
        is_active: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      setIsAddModalOpen(false);
      setNewUsername("");
      setNewPassword("");
      setNewUserRole("agent");
    },
    onError: (err: any) => {
      alert(`Failed to create user: ${err.message || "Unknown error"}`);
    },
  });

  // Update User Mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: Parameters<typeof updatePlatformUser>[2] }) =>
      updatePlatformUser(token, userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      setEditingUser(null);
      setResetPasswordUser(null);
      setNewResetPassword("");
    },
    onError: (err: any) => {
      alert(`Failed to update user: ${err.message || "Unknown error"}`);
    },
  });

  // Delete User Mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deletePlatformUser(token, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (err: any) => {
      alert(`Failed to delete user: ${err.message || "Unknown error"}`);
    },
  });


  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.tenant_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "Active" && u.is_active) ||
      (statusFilter === "Suspended" && !u.is_active);
    const matchesTenant =
      selectedTenantFilter === "all" || u.tenant_id === selectedTenantFilter;
    return matchesSearch && matchesRole && matchesStatus && matchesTenant;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-1">
            <span>Unlimited Team Agents Included</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="font-mono font-bold">Zero Per-Seat Fees</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-emerald-600" />
            Team &amp; Agents Management
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Invite your sales reps, support staff, and managers with dedicated logins. Never pay per-seat subscription fees.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex flex-col items-end text-right">
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Active Team Seats</span>
            <span className="font-mono text-xs font-bold text-slate-800">{users.length} / ∞ (Unlimited Included)</span>
          </div>
          <button
            onClick={() => {
              setNewUserRole("tenant");
              setSelectedTenantId(user.tenant_id || tenants[0]?.id || "");
              setIsAddModalOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Team Agent</span>
          </button>
        </div>
      </div>


      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search by username or tenant..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none font-semibold"
          >
            <option value="all">⊕ Role: All Roles</option>
            <option value="agent">Support / Sales Agent (Workspace)</option>
            <option value="tenant">Organization Manager</option>
            {isSuperAdmin && <option value="admin">Super Admin (Global)</option>}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Status: All</option>
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
          </select>

          {/* Linked Tenant Filter (Visible to Super Admin) */}
          {isSuperAdmin && (
            <select
              value={selectedTenantFilter}
              onChange={(e) => setSelectedTenantFilter(e.target.value)}
              className="filter-chip cursor-pointer outline-none"
            >
              <option value="all">🏢 Organization: All Organizations</option>
              {tenants.map((t: Tenant) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}

          {/* Clear Filters */}
          {(searchQuery || roleFilter !== "all" || statusFilter !== "all" || selectedTenantFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setRoleFilter("all");
                setStatusFilter("all");
                setSelectedTenantFilter("all");
              }}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold px-2 py-1"
            >
              Reset Filters
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => usersQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${usersQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="modern-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>User Account</th>
                <th>Access Role</th>
                <th>Assigned Organization</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created At</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-slate-500 font-medium">Loading platform user directory...</span>
                      <span className="text-[11px] text-slate-400">Fetching Admin, Manager, and Agent role assignments</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400 text-xs font-semibold">
                    No users found matching current filters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u: PlatformUserItem) => (
                  <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Username */}
                    <td>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          u.role === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : u.role === "agent"
                            ? "bg-sky-100 text-sky-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}>
                          {u.username.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-bold text-slate-900 text-xs">{u.username}</span>
                          <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
                            <button onClick={() => copyText(u.id)} title="Copy ID" className="hover:text-slate-700">
                              <Copy className="w-2.5 h-2.5" />
                            </button>
                            <span>{u.id.slice(0, 8)}...</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td>
                      {u.role === "admin" ? (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                          <ShieldAlert className="w-3 h-3 text-purple-600" />
                          Super Admin
                        </span>
                      ) : u.role === "agent" ? (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                          <Users className="w-3 h-3 text-sky-600" />
                          Support Agent
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-emerald-600" />
                          Org Manager
                        </span>
                      )}
                    </td>

                    {/* Linked Organization */}
                    <td>
                      <span className="text-xs font-semibold text-slate-800">
                        {u.tenant_name || (u.role === "admin" ? "Global System" : "Primary Tenant")}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <span className={`status-pill ${u.is_active ? "status-pill--connected" : "status-pill--disconnected"}`}>
                        {u.is_active ? "Active" : "Suspended"}
                      </span>
                    </td>

                    {/* Last Login */}
                    <td className="font-mono text-xs text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>

                    {/* Created At */}
                    <td className="font-mono text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="flex items-center gap-1.5">
                        {isSuperAdmin && (
                          <button
                            title="Edit Role & Tenant"
                            className="p-1.5 rounded-lg border border-slate-200 hover:border-blue-500 text-slate-500 hover:text-blue-600 transition-colors"
                            onClick={() => {
                              setEditingUser(u);
                              setEditRole(u.role);
                              setEditTenantId(u.tenant_id || "");
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {isSuperAdmin && (
                          <button
                            title="Reset Password"
                            className="p-1.5 rounded-lg border border-slate-200 hover:border-amber-500 text-slate-500 hover:text-amber-600 transition-colors"
                            onClick={() => {
                              setResetPasswordUser(u);
                              setNewResetPassword("");
                            }}
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </button>
                        )}

                        <button
                          title={u.is_active ? "Suspend User" : "Activate User"}
                          className={`p-1.5 rounded-lg border transition-colors ${
                            u.is_active
                              ? "border-slate-200 hover:border-amber-500 text-slate-500 hover:text-amber-600"
                              : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          }`}
                          onClick={() => {
                            updateUserMutation.mutate({
                              userId: u.id,
                              payload: { is_active: !u.is_active },
                            });
                          }}
                          disabled={updateUserMutation.isPending}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>

                        {isSuperAdmin && (
                          <button
                            title="Delete User"
                            className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete user "${u.username}"?`)) {
                                deleteUserMutation.mutate(u.id);
                              }
                            }}
                            disabled={deleteUserMutation.isPending}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}

            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Create Platform User
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Type / Access Role *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewUserRole("agent")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      newUserRole === "agent"
                        ? "bg-sky-50 border-sky-500 text-sky-900 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Users className="w-4 h-4 text-sky-600" />
                      Support / Sales Agent
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                      Dedicated Agent Panel (Live Inbox, CRM, Quick Replies)
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewUserRole("tenant")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      newUserRole === "tenant"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold shadow-2xs"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Organization Manager
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">
                      Full control (Billing, Instances, Team, Campaigns)
                    </p>
                  </button>

                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => setNewUserRole("admin")}
                      className={`p-2.5 rounded-lg border text-left col-span-1 sm:col-span-2 transition-all ${
                        newUserRole === "admin"
                          ? "bg-purple-50 border-purple-500 text-purple-900 font-bold shadow-2xs"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <ShieldAlert className="w-4 h-4 text-purple-600" />
                        Super Admin
                      </div>
                      <p className="text-[10px] text-slate-500 font-normal mt-0.5">Global platform control plane</p>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  placeholder="e.g. sales_rahul or support_agent1"
                  className="modern-input w-full text-xs font-mono"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  className="modern-input w-full text-xs font-mono"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              {newUserRole !== "admin" && isSuperAdmin && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Linked Organization / Tenant *
                  </label>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    className="modern-input w-full text-xs font-semibold"
                  >
                    {tenants.map((t: Tenant) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}


              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createUserMutation.mutate()}
                  disabled={!newUsername.trim() || !newPassword.trim() || createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? "Creating..." : "Save User Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}

      {editingUser && (
        <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Pencil className="w-4 h-4 text-blue-600" />
                Edit User: {editingUser.username}
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Role
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditRole("agent")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      editRole === "agent"
                        ? "bg-sky-50 border-sky-500 text-sky-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Users className="w-4 h-4 text-sky-600" />
                      Support Agent
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditRole("tenant")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      editRole === "tenant"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Manager
                    </div>
                  </button>

                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => setEditRole("admin")}
                      className={`p-2.5 rounded-lg border text-left col-span-2 transition-all ${
                        editRole === "admin"
                          ? "bg-purple-50 border-purple-500 text-purple-900 font-bold"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 text-xs">
                        <ShieldAlert className="w-4 h-4 text-purple-600" />
                        Super Admin
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {editRole === "tenant" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Linked Organization / Tenant
                  </label>
                  <select
                    value={editTenantId}
                    onChange={(e) => setEditTenantId(e.target.value)}
                    className="modern-input w-full text-xs font-semibold"
                  >
                    {tenants.map((t: Tenant) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => {
                    updateUserMutation.mutate({
                      userId: editingUser.id,
                      payload: {
                        role: editRole,
                        tenant_id: editRole === "tenant" ? editTenantId || undefined : undefined,
                      },
                    });
                  }}
                  disabled={updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="modal-backdrop" onClick={() => setResetPasswordUser(null)}>
          <div className="modal-card max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-amber-600" />
                Reset Password
              </h3>
              <button onClick={() => setResetPasswordUser(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <p className="text-xs text-slate-600">
                Set a new password for user <strong>{resetPasswordUser.username}</strong>:
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  className="modern-input w-full text-xs font-mono"
                  value={newResetPassword}
                  onChange={(e) => setNewResetPassword(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setResetPasswordUser(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => {
                    updateUserMutation.mutate({
                      userId: resetPasswordUser.id,
                      payload: { password: newResetPassword.trim() },
                    });
                  }}
                  disabled={newResetPassword.trim().length < 6 || updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? "Resetting..." : "Update Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

